<template>
    <WCard class="EntityBuilder">
        <div class="Header flex flex-1 row-stretch-center mb-1">
            <Tabs
                v-if="tabs.length > 1"
                v-model="activeTab"
                :tabs="tabs"
                @input="handleChangeTab"
            />
            <div class="Actions">
                <FadeTransition mode="out-in">
                    <WButton
                        v-if="activeTab === tabs[0].key"
                        iconLeft="save"
                        outline
                        color="accent-blue"
                        class="mr-1"
                        @click="handleClickSaveOrder"
                    >
                        {{ $t('schema.saveOrder') }}
                    </WButton>
                </FadeTransition>
                <WButton
                    v-if="activeTab === tabs[2].key"
                    iconLeft="plus-circle"
                    @click="newGroupModalOpen = true"
                >
                    {{ $t('schema.newGroup') }}
                </WButton>
                <WButton
                    v-if="[tabs[0].key, tabs[1].key].includes(activeTab)"
                    iconLeft="plus-circle"
                    @click="handleClickNew"
                >
                    {{ $t('schema.newProperty') }}
                </WButton>
            </div>
        </div>

        <FadeTransition mode="out-in">
            <div
                v-if="activeTab === tabs[0].key"
                :key="activeTab"
            >
                <SchemaBuilder
                    :ref="tabs[0].key"
                    v-bind="schema"
                    :entityKey="entityKey"
                    :isFetching="isFetching"
                    class="Item"
                    @onFetchData="getSchema"
                />
            </div>
            <div
                v-if="activeTab === tabs[1].key"
                :key="activeTab"
            >
                <MatchRules
                    :ref="tabs[1].key"
                    v-bind="schema"
                    :entityKey="entityKey"
                    :isFetching="isFetching"
                    class="Item"
                    @onFetchData="getSchema"
                />
            </div>
            <div
                v-if="activeTab === tabs[2].key"
                :key="activeTab"
                class="Groups"
            >
                <GroupsManaging
                    :entityKey="entityKey"
                    :groups="schema.groups"
                    :fields="schema.rows"
                    @refreshGroups="getSchema"
                />
                <NewGroupModal
                    :isOpen="newGroupModalOpen"
                    :entityKey="entityKey"
                    @refreshGroups="getSchema"
                    @close="newGroupModalOpen = false"
                />
            </div>
            <div
                v-if="activeTab === tabs[3].key"
                :key="activeTab"
            >
                <PopupBuilder
                    v-if="!isFetching"
                    :entityKey="entityKey"
                    :properties="schema.popupProperties"
                    :fields="schema.rows"
                    @refreshProperties="getSchema"
                />
            </div>
        </FadeTransition>
    </WCard>
</template>

<script>
import VueTypes from 'vue-types'
import {
    Api, apiRequest, rethrowError, notifyError,
} from '@/api'
import { Tabs } from '@/components'
import {
    SchemaBuilder, MatchRules, GroupsManaging, NewGroupModal, PopupBuilder,
} from './sections'

const SELECT_GROUPS = {
    TYPES: {
        equal: 'Equal',
        levenshtein: 'Levenshtein',
    },
}

export default {
    name: 'EntityBuilder',
    components: {
        Tabs,
        SchemaBuilder,
        MatchRules,
        GroupsManaging,
        NewGroupModal,
        PopupBuilder,
    },
    props: {
        entityKey: VueTypes.string,
    },
    data () {
        const { tab } = this.$route.query
        const tabs = [
            {
                key: 'schema',
                text: this.$t('schema.tabs.schema'),
            },
            {
                key: 'matchRules',
                text: this.$t('schema.tabs.matchRules'),
            },
            {
                key: 'groups',
                text: this.$t('schema.tabs.groups'),
            },
            {
                key: 'popup',
                text: this.$t('schema.tabs.popup'),
            },
        ]

        return {
            schema: {},
            isFetching: true,

            tabs,
            activeTab: tab || tabs[0].key,
            newGroupModalOpen: false,
        }
    },
    mounted () {
        this.getSchema()
    },
    methods: {
        handleChangeTab (tab) {
            const navigation = !tab ? { ...this.$router } : { query: { tab } }
            this.$router.replace(navigation).catch(() => {})
        },
        handleClickNew () {
            const ref = this.$refs[this.activeTab] || {}
            if (ref.onNewRow) ref.onNewRow()
        },
        handleClickSaveOrder () {
            const ref = this.$refs[this.activeTab] || {}
            if (ref.onSaveOrder) ref.onSaveOrder()
        },
        normalizeSchema ({
            fields, groups, matchRules, popupProperties,
        } = {}) {
            const normalizedGroups = groups.reduce((acc, group) => ({ ...acc, [group.key]: group.name }), {})
            const normalizedFields = fields.reduce((acc, field) => ({ ...acc, [field.key]: field.name }), {})

            return {
                rows: fields,
                selectGroups: {
                    groups: normalizedGroups,
                    fields: normalizedFields,
                    types: SELECT_GROUPS.TYPES,
                },
                groups,
                matchRules,
                popupProperties,
            }
        },
        async getSchema () {
            apiRequest(Api().entities.schema({ entityKey: this.entityKey }))
                .then(({ data }) => (this.schema = this.normalizeSchema(data)))
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isFetching = false))
        },
    },
}
</script>
<style lang="scss" scoped>
$tabs-height: 38px;
$grid-space: 88px;
$grid-height: $tabs-height + $grid-space + 15;

.EntityBuilder {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.Tabs {
	height: $tabs-height;
}

.Groups {
    margin-top: 15px;
}

.Item {
    &::v-deep {
        .DataGrid {
            @include admin-screen-height($grid-height);
        }
    }
}
</style>
